import React, { useState, useEffect, useCallback } from 'react';
import { IProvider } from 'src/types';
import { getFromLocalStorage, setToLocalStorage, removeFromLocalStorage } from 'src/utils';

const AUTH_STORAGE_KEY = '::@@omegaoneAuthToken@@::';

interface IAuthContext {
  accessToken: string;
  userId: string | null;
  isAuthenticated: boolean;
  setAuth: (token: string, userId: string) => void;
  clearAuth: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const AuthContext = React.createContext<IAuthContext>({
  accessToken: '',
  userId: null,
  isAuthenticated: false,
  setAuth: noop,
  clearAuth: noop,
});

export const AuthContextProvider = ({ children }: IProvider) => {
  const [accessToken, setAccessToken] = useState('');
  const [userId, setUserId] = useState<string | null>(null);

  // On mount, restore auth from localStorage
  useEffect(() => {
    const stored = getFromLocalStorage(AUTH_STORAGE_KEY);
    if (!stored) return;

    try {
      const { accessToken: token, userId: uid } = JSON.parse(stored);
      if (token && uid) {
        // Decode JWT payload to check expiration
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp > now) {
          setAccessToken(token);
          setUserId(uid);
        } else {
          removeFromLocalStorage(AUTH_STORAGE_KEY);
        }
      }
    } catch {
      removeFromLocalStorage(AUTH_STORAGE_KEY);
    }
  }, []);

  const setAuth = useCallback((token: string, uid: string) => {
    setAccessToken(token);
    setUserId(uid);
    setToLocalStorage(AUTH_STORAGE_KEY, { accessToken: token, userId: uid });
  }, []);

  const clearAuth = useCallback(() => {
    setAccessToken('');
    setUserId(null);
    removeFromLocalStorage(AUTH_STORAGE_KEY);
  }, []);

  const isAuthenticated = accessToken !== '' && userId !== null;

  return (
    <AuthContext.Provider
      value={{ accessToken, userId, isAuthenticated, setAuth, clearAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};
